import * as Yup from "yup";
import * as animationData from "src/assets/animated/loader.json";

// validations
export const schema = Yup.object().shape({
  mode: Yup.string()
    .required("Required")
    .oneOf(["add", "edit"] as const),
  journey_type: Yup.string()
    .required("Required")
    .oneOf(["manual", "foodics", "excel"] as const),

  onboarding: Yup.string()
    .required("Required")
    .oneOf(["yes", "no"] as const),

  // company details
  company_name: Yup.string().required("Required"),
  branch_name: Yup.string().required("Required"),
  city_id: Yup.string().required("Required"),
  has_vat: Yup.string().required("You have to select an option"),

  vat: Yup.string().when("has_vat", {
    is: "yes",
    then: (value) => value.required("Required"),
  }),

  branch_id: Yup.string().required("Required"),
  branchConnectedToFoodics: Yup.boolean(),

  // calculate sales
  orderByItems: Yup.string().when("mode", {
    is: "add",
    then: (value) => value.required("Required"),
  }),
  orderByPayment: Yup.string().when("mode", {
    is: "add",
    then: (value) => value.required("Required"),
  }),
  branchHasMenuFeature: Yup.boolean(),
  branchHasItemsFeature: Yup.boolean(),
  orders: Yup.string().when("mode", {
    is: "add",
    then: (value) => value.required("Required"),
  }),
  excelSales: Yup.string().when("mode", {
    is: "add",
    then: (value) => value.required("Required"),
  }),
  productSales: Yup.string().when(
    ["branchHasMenuFeature", "mode", "journey_type"],
    {
      // file of product sales and menu package
      is: (branchHasMenuFeature: boolean, mode: string, journey_type: string) =>
        branchHasMenuFeature === true &&
        mode === "add" &&
        (journey_type === "manual" || journey_type === "excel"),
      then: (schema) => schema.required("Required"),
    },
  ),
  discount: Yup.string(),
  tips: Yup.string(),

  // COGS
  COGS_manual_type: Yup.string()
    .required("Required")
    .oneOf(["percentage", "amount"] as const),

  COGS_value: Yup.number()
    .typeError("Required")
    .required("Required")
    .when("COGS_manual_type", {
      is: "percentage",
      then: (value) => value.max(100, "Max value is 100"),
    }),

  // has aggregators
  has_aggregators: Yup.string().required("You have to select an option"),
  // aggregators
  aggregators: Yup.array()
    .of(
      Yup.object().shape({
        aggregator_name: Yup.string()
          .required("Required")
          // .min(3, "Must be at least 3 characters long"),
          .test(
            "min-3-non-whitespace",
            "Must be at least 3 characters long",
            (value) => !!value && value.trim().length >= 3,
          )
          .test(
            "unique-aggregator-name",
            "You have an aggregator with the same name already",
            (value: any, context) => {
              const allData = context.from?.[1].value;
              const options: any = context.options;
              const currentIndex = options.index;

              if (
                allData.aggregators.length <= 1 ||
                allData.onboarding === "no"
              ) {
                return true; // No previous items to compare if only one item
              }

              if (currentIndex === allData.aggregators.length - 1) {
                // Check all previous items for a duplicate aggregator_name
                for (let i = 0; i < allData.aggregators.length - 1; i++) {
                  if (
                    allData.aggregators[i].aggregator_name.trim() ===
                    value.trim()
                  ) {
                    return false; // Duplicate found
                  }
                }
              }

              return true;
            },
          ),
        aggregator_fee: Yup.string()
          .required("Required")
          .test(
            "min-value",
            "Must be positive number",
            (value) => parseFloat(value || "") >= 0,
          )
          .test("max-value", "Max is 100", (value) => parseFloat(value) <= 100),
        payments: Yup.array()
          .of(Yup.string().required(""))
          .test(
            "conditionally-required",
            "Select related payments",
            (value: any, context) => {
              const journey = context?.from
                ? context.from?.[1]?.value.journey_type
                : undefined;
              const onboardingJourney = context?.from
                ? context.from?.[1]?.value.onboarding
                : undefined;
              return (
                value?.length > 0 ||
                journey !== "foodics" ||
                onboardingJourney !== "yes"
              );
            },
          ),
        delivery_type: Yup.string()
          .required("Required")
          .oneOf(["amount", "order"] as const),
        delivery_cost: Yup.string().test(
          "min-value",
          "Must be positive number",
          (value) => parseFloat(value ?? "") >= 0 || value === "",
        ),
        // manual only
        net_sales_with_tax: Yup.mixed().test(
          "conditionally-required",
          "Net sales with tax is required",
          (value: any, context) => {
            const journey = context?.from
              ? context.from?.[1]?.value.journey_type
              : undefined;
            return value || journey !== "manual";
          },
        ),
        order_quantity: Yup.mixed().test(
          "conditionally-required",
          "Order quantity is required",
          (value: any, context) => {
            const journey = context?.from
              ? context.from?.[1]?.value.journey_type
              : undefined;
            return value || journey !== "manual";
          },
        ),
        aggregator_id: Yup.string(),
        aggregator_sale_id: Yup.string(),
        payments_Names: Yup.array().of(Yup.string()),
        is_keta: Yup.boolean(),
        has_afc: Yup.boolean(),
      }),
    )
    .required(""),

  //balance
  balance: Yup.array()
    .of(
      Yup.object().shape({
        amount: Yup.string(),
        quantity: Yup.string(),
        id: Yup.string(),
        name: Yup.string(),
        agg_id: Yup.string(),
        payment_method_id: Yup.string(),
        is_branch: Yup.boolean(),
        start_balance: Yup.string(),
        is_new_payment: Yup.boolean(),
        is_discount: Yup.boolean(),
      }),
    )
    .required("Required"),

  // Manual
  net_sales_with_tax: Yup.string().required("Required"),
  total_order_quantity: Yup.string().required("Required"),
  dateRange: Yup.object().shape({
    start_date: Yup.string().required("Required"),
    end_date: Yup.string()
      .required("Required")
      .test(
        "date-range",
        "Maximum adding sales days is 31 days for each sales journey",
        function (endDate) {
          const startDate = this.parent.start_date;
          if (!startDate || !endDate) return true; // Allow empty values, leave validation to required()

          const startDateObj = new Date(startDate);
          const endDateObj = new Date(endDate);
          const differenceInTime =
            endDateObj.getTime() - startDateObj.getTime();
          const differenceInDays = differenceInTime / (1000 * 3600 * 24);

          return differenceInDays <= 31;
        },
      ),
  }),

  //Foodics only
  has_jazr_calculations: Yup.string()
    .required("")
    .oneOf(["yes", "no"] as const),

  hasOtherRevenue: Yup.string().required("You have to select an option"),
  other_revenue: Yup.string().when("hasOtherRevenue", {
    is: "yes",
    then: (value) =>
      value
        .required("Required")
        .test("min-value", "Must be greater than 0", (value) => {
          if (!value) return true;
          return parseFloat(value) > 0;
        }),
  }),

  include_other_revenue_tax: Yup.string(),
  other_revenue_tax: Yup.string().when("hasOtherRevenue", {
    is: "yes",
    then: (value) => value.required("Required"),
  }),

  otherRevenuePayments: Yup.array()
    .of(
      Yup.object().shape({
        payment_method_id: Yup.string().required("Required"),
        amount: Yup.string()
          .required("Required")
          .test("min-value", "Must be greater than 0", (value) => {
            if (!value) return true; // Skip validation if the value is empty
            return parseFloat(value) > 0;
          }),
      }),
    )
    .required("Required"),
  // payment discount
  hasPaymentDiscount: Yup.string().required("You have to select an option"),
  paymentDiscount: Yup.string().when("hasPaymentDiscount", {
    is: "yes",
    then: (value) =>
      value
        .required("Required")
        .test("min-value", "Must be greater than 0", (value) => {
          if (!value) return true;
          return parseFloat(value) > 0;
        }),
  }),
  discountPayments: Yup.array()
    .of(
      Yup.object().shape({
        pay_id: Yup.string(),
        payment_method_id: Yup.string().required("Required"),
        amount: Yup.string()
          .required("Required")
          .test("min-value", "Must be greater than 0", (value) => {
            if (!value) return true; // Skip validation if the value is empty
            return parseFloat(value) > 0;
          }),
        name: Yup.string(),
      }),
    )
    .required("Required"),
});

export interface FormData extends Yup.InferType<typeof schema> {}

export const initSteps = [
  {
    id: "company_details",
    name: "Company details",
    fields: ["company_name", "branch_name", "city_id", "has_vat", "vat"],
  },
  {
    id: "calculate_sales",
    name: "Calculate sales",
    fields: [
      "orderByItems",
      "orderByPayment",
      "orders",
      "other_revenue",
      "other_revenue_tax",
      "otherRevenuePayments",
    ], // manual fields are overwritten inside index file
  },
  {
    id: "calculate_COGS",
    name: "Calculate COGS",
    fields: ["COGS_value", "COGS_manual_type"],
  },
  {
    id: "make_balance",
    name: "Make balance",
    fields: [],
  },
  {
    id: "summary",
    name: "Summary",
    fields: [],
  },
];

export const defaultLottieOptions = {
  loop: true,
  autoplay: true,
  animationData: animationData,
  rendererSettings: {
    preserveAspectRatio: "xMidYMid slice",
  },
};

export const professionalAccountantFeatures = [
  "savedCostOfProfessionalAccountant",
  "accountingSoftwareForRestaurantsAndCafes",
  "comprehensiveReportsForRestaurantOwners",
  "specialReportsForAggregatorsApps",
  "taxReportsWithHighAccuracy",
  "accurateCashFlowReports",
  "readyReportsForTaxInspection",
  "clearOwedAmounts",
  "automaticAssetDepreciation",
  "automaticInvoiceReminders",
  "forecastingFutureExpensesAccurately",
  "kpisForRestaurantOwner",
  "freeEmployeeTraining",
  "integrationWithPOS",
  "automaticCalculationOfCOGS",
  "abilityToAddCOGSFormula",
];

export const professionalAndBenefitsAccountantFeatures = [
  "professionalAccountantBenefits",
  "enterItemsWithPurchasingInvoices",
  "trackingItemCostOverTime",
  "ensuringCorrectPurchasingEntry",
  "inventoryStocktakingAndReporting",
  "automaticYieldRatioCalculation",
  "accurateFIFOInventoryCalculation",
  "inventoryDataReviewBeforeApproval",
  "distinguishingBetweenActualWasteAndOtherCauses",
];

export const photographInvoicesAndRelaxFeatures = [
  "professionalAndCostAccountantBenefits",
  "dedicatedAppForInvoices",
  "dedicatedStaffAndManager",
  "freeSetup",
  "bankCashReconciliation",
  "integratedMonthlyStatements",
  "accurateDataEntry",
];

export const menuRecipeAndProductionFeatures = [
  "previousPackageBenefitsFeature",
  "menuRecipesFeature",
  "modifierRecipesFeature",
  "removeRecipeItemsFeature",
  "productionInstructionsFeature",
  "consumptionReportsFeature",
];
